import { useState, useEffect } from 'react'
import { X, Loader2, ArrowRight, Check } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { useAllUsers } from '@/lib/useAllUsers'
import { formatDate, parsePics, joinPics } from '@/lib/utils'
import type { Task, TaskPriority, TaskStatus } from '@/lib/types'

interface ReassignEntry { fromName: string | null; toName: string | null; reason: string | null; by: string | null; at: string }

interface Props {
  task: Task
  onClose: () => void
}

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'not-started', label: 'Not Started' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
]

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]

export function EditTaskModal({ task, onClose }: Props) {
  const { updateTask, getProject } = useApp()
  const { users: allUsers } = useAllUsers()
  const proj = getProject(task.projectId)
  const projectPic = proj?.pic ?? null
  const projectDept = proj?.department ?? null
  const [title, setTitle] = useState(task.title)
  const [description, setDescription] = useState(task.description)
  const [priority, setPriority] = useState<TaskPriority>(task.priority)
  const [status, setStatus] = useState<TaskStatus>(task.status)
  // PIC task berupa DAFTAR nama (digabung ';'). Dulu <select> tunggal di sini
  // menimpa seluruh daftar dengan satu nama, jadi memilih PIC lewat modal ini
  // diam-diam mencabut rekan yang lain.
  const [pics, setPics] = useState<string[]>(parsePics(task.assignee))
  const [dueDate, setDueDate] = useState<string>(task.dueDate?.slice(0, 10) ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [reason, setReason] = useState('')
  const [history, setHistory] = useState<ReassignEntry[]>([])

  // Pemindahan = ada PIC lama dan orangnya berubah.
  //
  // Dibandingkan setelah dinormalkan, dengan aturan yang SAMA dengan server
  // (urutan dan huruf besar-kecil diabaikan). Membandingkan nilai mentah dengan
  // hasil joinPics membuat task yang tersimpan sebagai "Ana; Budi" dianggap
  // berubah begitu modal dibuka, padahal belum disentuh.
  const crewKey = (raw: string | null | undefined) =>
    parsePics(raw).map((n) => n.toLowerCase()).sort().join(';')
  const original = task.assignee ?? ''
  const assignee = joinPics(pics) ?? ''
  const isReassign = !!task.assignee && crewKey(task.assignee) !== crewKey(assignee)

  useEffect(() => {
    fetch(`/api/reassignments?entityType=task&entityId=${task.id}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setHistory(Array.isArray(d) ? d : []))
      .catch(() => {})
  }, [task.id])

  // Daftar PIC: SEMUA user (lintas departemen) + PIC saat ini. Simpan dept per nama.
  const picOptions = (() => {
    const map = new Map<string, string | null>()
    for (const u of allUsers) if (u.username) map.set(u.username, u.department)
    if (task.assignee && !map.has(task.assignee)) map.set(task.assignee, null)
    return Array.from(map.entries())
      .map(([username, department]) => ({ username, department }))
      .sort((a, b) => a.username.localeCompare(b.username))
  })()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || submitting) return
    setSubmitting(true)
    try {
      await updateTask(task.id, {
        title: title.trim(),
        description: description.trim(),
        priority,
        status,
        assignee: joinPics(pics),
        dueDate: dueDate || null,
        ...(isReassign && reason.trim() ? { reason: reason.trim() } : {}),
      })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass =
    'w-full px-3.5 py-2.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl text-[#111110] text-sm font-body focus:outline-none focus:border-[#111110] focus:bg-white transition-all'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in"
        onClick={onClose}
      />

      <div className="relative w-full max-w-[480px] bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 animate-slide-up">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#F2F1EC]">
          <h2 className="font-display text-lg font-bold text-[#111110]">Edit Task</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#111110] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
              Judul Task
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={inputClass}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
              Deskripsi
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className={`${inputClass} resize-none leading-relaxed`}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as TaskStatus)}
                className={inputClass}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">Prioritas</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as TaskPriority)}
                className={inputClass}
              >
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
                PIC <span className="text-[#8A8A85] font-normal text-xs">boleh lebih dari satu</span>
              </label>
              <div className="max-h-40 overflow-auto bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl py-1">
                {picOptions.length === 0 && (
                  <p className="px-3 py-1.5 text-xs text-[#AEADA8] font-body">Belum ada user terdaftar</p>
                )}
                {picOptions.map((o) => {
                  const on = pics.some((n) => n.toLowerCase() === o.username.toLowerCase())
                  return (
                    <button
                      key={o.username}
                      type="button"
                      onClick={() => setPics((cur) => on
                        ? cur.filter((n) => n.toLowerCase() !== o.username.toLowerCase())
                        : [...cur, o.username])}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs font-body transition-colors ${
                        on ? 'text-[#1A1916] font-semibold' : 'text-[#6B6860] hover:bg-[#F0EFEA]'
                      }`}
                    >
                      <span
                        className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-all"
                        style={{ borderColor: on ? '#6366F1' : '#C8C7BF', backgroundColor: on ? '#6366F1' : 'white' }}
                      >
                        {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                      </span>
                      <span className="flex-1 text-left truncate">
                        {o.department && o.department !== projectDept ? `${o.username} · ${o.department}` : o.username}
                      </span>
                    </button>
                  )
                })}
              </div>
              {pics.length === 0 && (
                <p className="mt-1 text-[11px] text-[#AEADA8] font-body">
                  {projectPic ? `Kosong = ikut PIC project (${projectPic})` : 'Kosong = tanpa PIC'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">Deadline</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {isReassign && (
            <div>
              <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
                Alasan pemindahan <span className="text-[#AEADA8] font-normal text-xs">opsional</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder={`Kenapa dipindah dari "${original}" ke "${assignee || 'tanpa PIC'}"?`}
                className={`${inputClass} resize-none leading-relaxed`}
              />
            </div>
          )}

          {history.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#C0BDB6] font-body font-semibold mb-2">Riwayat Pemindahan</p>
              <ul className="space-y-1.5 max-h-32 overflow-y-auto">
                {history.map((h, i) => (
                  <li key={i} className="text-[11px] font-body bg-[#F8F7F3] border border-[#F0EFE9] rounded-lg px-2.5 py-1.5">
                    <span className="flex items-center gap-1 text-[#1A1916] font-medium">
                      {h.fromName || 'tanpa PIC'} <ArrowRight className="w-3 h-3 text-[#AEADA8]" /> {h.toName || 'tanpa PIC'}
                    </span>
                    <span className="block text-[#8A8A85] mt-0.5">
                      {h.reason ? <>&ldquo;{h.reason}&rdquo; · </> : null}{h.by || 'tanpa nama'} · {formatDate(h.at)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-body text-[#8A8A85] hover:text-[#111110] transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={!title.trim() || submitting}
              className="flex items-center gap-2 px-5 py-2 bg-[#111110] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2A2A28] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
